import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';

import { useAmpContext } from 'utils/AmpContext';
import { getWebpSrc } from 'utils/filestack';
import { useElementReachedEffect } from 'utils/screen';

type Props = {
  height?: number;
  width?: number;
  expandSize?: boolean;
  srcSet?: string | undefined;
  alt?: string;
  'data-qa-id'?: string;
  svg?: boolean;
  webp?: boolean;
  quality?: number;
  objectFit?: 'cover' | 'contain';
  alwaysShow?: boolean;
  className?: string;
  src: string;
  layout?: string;
  onLoad?: () => void;
};

function ImageWithRef({
  className,
  layout,
  src,
  srcSet,
  alwaysShow,
  onLoad,
  quality,
  svg,
  height,
  width,
  expandSize,
  alt,
  webp,
  objectFit,
  'data-qa-id': dataQaId,
  forwardedRef,
}: Props & { forwardedRef: ForwardedRef<HTMLImageElement> }) {
  const [show, setShow] = useState(Boolean(alwaysShow));
  const lazyRef = useRef<HTMLDivElement>(null);

  useElementReachedEffect({
    ref: lazyRef,
    offset: { top: 200 },
    onReach: useCallback(() => setShow(true), []),
  });

  const isAmp = useAmpContext();
  if (isAmp) {
    return (
      <amp-img
        layout={layout}
        src={src}
        srcSet={srcSet}
        class={className}
        height={height}
        width={width}
        alt={alt}
        webp={webp}
        data-qa-id={dataQaId}
      />
    );
  }

  if (!show) return <div ref={lazyRef} className={className} />;

  const webpSrc = getWebpSrc(srcSet || src, {
    alwaysConvert: webp,
    quality,
  });

  return (
    <picture>
      {!svg && <source srcSet={webpSrc} type="image/webp" />}

      <source srcSet={srcSet || src} />

      <img
        className={className}
        src={src}
        alt={alt || ''}
        ref={forwardedRef}
        height={expandSize ? '100%' : height}
        width={expandSize ? '100%' : width}
        onLoad={onLoad}
        data-qa-id={dataQaId}
        style={{ objectFit }}
      />
    </picture>
  );
}

export const Image = forwardRef<HTMLImageElement, Props>((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <ImageWithRef {...props} forwardedRef={ref} />
));
